import { buildApiUrl } from '../../../utils/createUrl';
import formatDateToYYYYMMDD from '../../../utils/formatDateToYYYYMMDD';
import addDate from '../../../utils/addDate';

/**
 * Calculates a 90-day window centered on the given date (or today if no date provided)
 * Returns dates formatted as YYYY-MM-DD strings
 * 
 * @param {Date|string|null} centerDate - Date to center the window on (defaults to today)
 * @returns {Object} Object with startDate and endDate as YYYY-MM-DD strings
 */
export const calculateDateWindow = (centerDate = null) => {
  // Use provided date or today
  let center = centerDate 
    ? (centerDate instanceof Date ? new Date(centerDate) : new Date(centerDate))
    : new Date();
  
  // Reset time to start of day
  center.setHours(0, 0, 0, 0);
  
  // Validate date
  if (isNaN(center.getTime())) {
    // If invalid, use today
    center = new Date();
    center.setHours(0, 0, 0, 0);
  }
  
  // Calculate 45 days before and 45 days after (90 days total)
  // Create new Date instances to avoid mutation
  const startDate = new Date(center);
  addDate(startDate, -45);
  
  const endDate = new Date(center);
  addDate(endDate, 45);
  
  // Format dates as YYYY-MM-DD strings
  return {
    startDate: formatDateToYYYYMMDD(startDate),
    endDate: formatDateToYYYYMMDD(endDate),
  };
};

/**
 * Builds the calendar API URL with query parameters
 * 
 * @param {Object} params
 * @param {string} params.origin - Origin slug (e.g., 'cdmx')
 * @param {string} params.destination - Destination slug (e.g., 'monterrey')
 * @param {string} params.sourceUrl - Source URL from config (e.g., 'www.reservamos.mx/api/v2')
 * @param {boolean} params.forceHttps - Force HTTPS protocol (optional, default: false)
 * @param {string} params.startDate - Start date for events range in YYYY-MM-DD format (optional)
 * @param {string} params.endDate - End date for events range in YYYY-MM-DD format (optional)
 * @returns {string|null} API URL or null if invalid
 */
export const buildCalendarApiUrl = ({
  origin,
  destination,
  sourceUrl,
  forceHttps = false,
  startDate,
  endDate,
}) => {
  if (!origin || !destination || !sourceUrl) {
    return null;
  }

  // Remove /places from sourceUrl if present (sourceUrl is used for autocomplete which includes /places)
  // For calendar API, we need /calendar instead
  const baseSourceUrl = sourceUrl.replace(/\/places$/, '');
  
  // Use helper function to build URL with protocol
  const url = buildApiUrl({ sourceUrl: baseSourceUrl, path: '/calendar', forceHttps });
  
  url.setQueryParam('origin', origin);
  url.setQueryParam('destination', destination);
  
  if (startDate) {
    url.setQueryParam('start_date', startDate);
  }
  
  if (endDate) {
    url.setQueryParam('end_date', endDate);
  }
  
  return url.href;
};

